import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import axiosInstance from "~/api/api.axiosInstance"
import { queryKeys } from "~/api/cache"
import type { PresentationLinkDto } from "~/api/dto/public-presentation.dto"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"

export const SharePresentation = (): JSX.Element => {
  const proposal = useProposalContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const proposalId = proposal.remote.id
  const key = queryKeys.publicPresentation.link(proposalId)
  const path = `/proposal/v1/${proposalId}/presentation-link`
  const link = useQuery({
    queryKey: key,
    queryFn: async () =>
      (await axiosInstance.get<PresentationLinkDto | null>(path)).data,
    enabled: !!proposalId,
  })
  const createLink = useMutation({
    mutationFn: async () =>
      (await axiosInstance.post<PresentationLinkDto>(path)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(key, data)
      setCopied(false)
      setCopyError(false)
    },
  })
  const copyLink = async () => {
    if (!link.data) return
    try {
      await navigator.clipboard.writeText(link.data.url)
      setCopied(true)
      setCopyError(false)
    } catch {
      setCopyError(true)
    }
  }
  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <p>{t(`publicPresentationDescription`)}</p>
      {link.isLoading && <p role="status">{t(`loadingPresentationLink`)}</p>}
      {(link.isError || createLink.isError) && (
        <p role="alert">{t(`presentationLinkError`)}</p>
      )}
      {link.data && (
        <>
          <label className="flex flex-col gap-2 font-medium">
            {t(`publicPresentationLink`)}
            <input
              className="w-full rounded border border-input bg-background px-3 py-2 text-foreground"
              readOnly
              value={link.data.url}
              onFocus={(event) => event.target.select()}
            />
          </label>
          <p className="text-sm">
            {t(`presentationLinkExpires`, {
              date: new Date(link.data.expiresAt).toLocaleDateString(),
            })}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="rounded bg-primary px-4 py-2 text-primary-foreground"
              onClick={() => void copyLink()}
            >
              {t(copied ? `presentationLinkCopied` : `copyPresentationLink`)}
            </button>
            <a
              className="rounded border border-input px-4 py-2"
              href={link.data.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(`openPresentation`)}
            </a>
          </div>
          {copyError && <p role="alert">{t(`presentationLinkCopyError`)}</p>}
        </>
      )}
      {!link.isLoading && !link.data && (
        <button
          className="self-start rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
          type="button"
          disabled={!proposalId || createLink.isLoading}
          onClick={() => createLink.mutate()}
        >
          {t(
            createLink.isLoading
              ? `creatingPresentationLink`
              : `createPresentationLink`,
          )}
        </button>
      )}
    </section>
  )
}
